import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Avatar
} from '@mui/material';
import { TrendingUp, CheckCircle, Cancel, Remove, SmartToy as AiIcon } from '@mui/icons-material';
import { getPicksByHandicapperSlug, getHandicapperStatsBySlug } from '../../services/picksService';
import { sportKeyToShort } from '../../utils/sportsMapping';
import { SportIcon } from '../../utils/sportIcons';
import BackButton from '../BackButton';

const HandicapperPicksScreen = () => {
  const { handicapperName } = useParams(); // This is actually the slug (e.g., 'yianni-skilitis')
  const [picks, setPicks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHandicapperData();
  }, [handicapperName]);

  const loadHandicapperData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load picks by handicapper slug (URL-friendly format)
      const picksData = await getPicksByHandicapperSlug(handicapperName);
      setPicks(picksData);

      // Get stats by handicapper slug
      const statsData = await getHandicapperStatsBySlug(handicapperName);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading handicapper data:', err);
      setError('Failed to load picks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'won':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'lost':
        return <Cancel sx={{ color: 'error.main' }} />;
      case 'push':
        return <Remove sx={{ color: 'warning.main' }} />;
      default:
        return <TrendingUp sx={{ color: 'info.main' }} />;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'push':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'error';
      case 'medium':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatPickType = (pickType) => {
    const typeMap = {
      'h2h': 'Moneyline',
      'spreads': 'Spread',
      'totals': 'Total'
      // Period markets temporarily hidden for development
      // 'h2h_h1': '1H Moneyline',
      // 'h2h_h2': '2H Moneyline',
      // 'spreads_h1': '1H Spread',
      // 'spreads_h2': '2H Spread',
      // 'totals_h1': '1H Total',
      // 'totals_h2': '2H Total'
    };
    return typeMap[pickType] || pickType;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading picks...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackButton />
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar 
          sx={{ 
            width: 60, 
            height: 60, 
            mr: 2, 
            bgcolor: picks.length > 0 && picks[0].personalityColor 
              ? picks[0].personalityColor 
              : 'primary.main' 
          }}
        >
          {picks.length > 0 && picks[0].aiGenerated ? (
            <AiIcon sx={{ fontSize: 32 }} />
          ) : (
            picks.length > 0 && picks[0].handicapperName 
              ? picks[0].handicapperName.charAt(0).toUpperCase() 
              : handicapperName.charAt(0).toUpperCase()
          )}
        </Avatar>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" component="h1">
              {picks.length > 0 && picks[0].handicapperName 
                ? picks[0].handicapperName 
                : handicapperName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Typography>
            {picks.length > 0 && picks[0].aiGenerated && picks[0].personalityName && (
              <Chip
                label={picks[0].personalityName}
                size="small"
                sx={{
                  bgcolor: picks[0].personalityColor || 'primary.main',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {picks.length > 0 && picks[0].aiGenerated ? 'AI-Powered Picks' : 'Handicapper Picks'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.totalPicks}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Picks
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="h4">{stats.wins}</Typography>
              <Typography variant="body2">
                Wins
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
              <Typography variant="h4">{stats.losses}</Typography>
              <Typography variant="body2">
                Losses
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
              <Typography variant="h4">{stats.winRate}%</Typography>
              <Typography variant="body2">
                Win Rate
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* No picks message */}
      {picks.length === 0 && (
        <Alert severity="info">
          No picks available from this handicapper yet.
        </Alert>
      )}

      {/* Picks List */}
      <Grid container spacing={1.5}>
        {picks.map((pick) => (
          <Grid item xs={12} key={pick.id}>
            <Card>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {pick.awayTeam} @ {pick.homeTeam}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<SportIcon sportKey={pick.sport} fontSize="small" />}
                        label={sportKeyToShort(pick.sport) || 'N/A'} 
                        size="small" 
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                      <Chip 
                        label={formatPickType(pick.pickType)} 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label={`Confidence: ${pick.confidence}`} 
                        size="small" 
                        color={getConfidenceColor(pick.confidence)}
                      />
                      {pick.aiGenerated && pick.personalityName && (
                        <Chip 
                          icon={<AiIcon sx={{ fontSize: 14 }} />}
                          label={pick.personalityName}
                          size="small" 
                          sx={{
                            bgcolor: pick.personalityColor || 'primary.main',
                            color: 'white',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }}
                        />
                      )}
                      <Chip 
                        icon={getResultIcon(pick.result)}
                        label={pick.result.toUpperCase()} 
                        size="small" 
                        color={getResultColor(pick.result)}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', ml: 2 }}>
                    <Typography variant="h6" color="primary">
                      {pick.pickedTeam}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pick.odds > 0 ? '+' : ''}{pick.odds}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Analysis:</strong>
                </Typography>
                <Typography variant="body2" paragraph>
                  {pick.analysis}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Posted: {pick.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default HandicapperPicksScreen;
